// Pure date helpers. No timezone library, no Node APIs — these run in a browser bundle and in
// the Workers runtime.
//
// Everything here treats an ISODate as a calendar day and pins it to 12:00 UTC when it needs a
// Date object, so a local-midnight offset can never roll a day backwards. That bug is silent and
// it shows up as "yesterday's entry", which is the worst possible way to find it.

import type { ISODate, Weekday } from './types.js'

export function toDate(date: ISODate): Date {
  return new Date(`${date}T12:00:00Z`)
}

export function toISODate(d: Date): ISODate {
  return d.toISOString().slice(0, 10)
}

export function addDays(date: ISODate, days: number): ISODate {
  const d = toDate(date)
  d.setUTCDate(d.getUTCDate() + days)
  return toISODate(d)
}

export function weekdayOf(date: ISODate): Weekday {
  return toDate(date).getUTCDay() as Weekday
}

/** Whole days from `from` to `to`. Negative when `to` is earlier. */
export function daysBetween(from: ISODate, to: ISODate): number {
  return Math.round((toDate(to).getTime() - toDate(from).getTime()) / 86_400_000)
}

/** The Monday of the week containing `date`. */
export function startOfWeek(date: ISODate): ISODate {
  const wd = weekdayOf(date)
  const backToMonday = wd === 0 ? 6 : wd - 1
  return addDays(date, -backToMonday)
}

/**
 * 1-based week of the term, counted in Monday-started weeks from the week instruction begins.
 * Returns 0 before the term starts.
 */
export function semesterWeek(date: ISODate, termStart: ISODate): number {
  if (daysBetween(date, termStart) > 0) return 0
  const weeks = daysBetween(startOfWeek(termStart), startOfWeek(date)) / 7
  return Math.floor(weeks) + 1
}

// ── Clock time ──────────────────────────────────────────────────────────────
//
// THE APP PRINTS 12-HOUR TIME. ALL OF IT. The owner reads am/pm natively and asked for it
// everywhere (2026-08-28); a screen that says "18:29" is making him do arithmetic to read his
// own timetable. There are exactly two display registers and one machine register:
//
//   `minutesToClock`        '5:00 PM'  — the default. Use it wherever the column has room.
//   `minutesToClockCompact` '5:00p'    — the same instant where it does not (Month cells, the
//                                        phone's 56px time column). `compactTime` in
//                                        calendar.ts is this function.
//   `minutesToInputTime`    '17:00'    — NOT DISPLAY. `<input type="time">` requires 24-hour
//                                        'HH:MM' in its `value`, and the browser renders it in
//                                        the reader's own locale anyway. Machine payloads too.
//
// A fourth register does exist and is deliberate: apps/web's `clockFromTimestamp` prints
// '11:59pm' / '9am', which is the LITERAL string the design handoff uses for deadline stamps
// (docs/design/handoff/LifeOS School.dc.html). It is already 12-hour, so it stays as it is.
//
// No `toLocaleTimeString` anywhere in here: these run in the Workers runtime as well as the
// browser, and demo screenshots have to be reproducible on any machine.

function hour12(minutes: number): number {
  const h24 = Math.floor(minutes / 60) % 24
  return h24 % 12 === 0 ? 12 : h24 % 12
}

function isPm(minutes: number): boolean {
  return Math.floor(minutes / 60) % 24 >= 12
}

/** '5:00 PM' · '12:00 AM' (midnight) · '12:30 PM' — minutes past midnight, read out loud. */
export function minutesToClock(minutes: number): string {
  const m = Math.floor(minutes) % 60
  return `${hour12(minutes)}:${String(m).padStart(2, '0')} ${isPm(minutes) ? 'PM' : 'AM'}`
}

/** '5:00p' — the same clock where the column is too narrow for a spelled-out meridiem. */
export function minutesToClockCompact(minutes: number): string {
  const m = Math.floor(minutes) % 60
  return `${hour12(minutes)}:${String(m).padStart(2, '0')}${isPm(minutes) ? 'p' : 'a'}`
}

/**
 * '17:00' — 24-hour 'HH:MM'. THE ONE PLACE 24-HOUR TIME SURVIVES, and it is not for reading:
 * `<input type="time">` only accepts this shape in `value`, and it localises the display itself.
 * If you are writing this into a `<span>`, you want `minutesToClock`.
 */
export function minutesToInputTime(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = Math.floor(minutes) % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

export function msToTimestamp(ms: number): string {
  const total = Math.floor(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export function weekdayName(date: ISODate, short = false): string {
  const n = WEEKDAY_NAMES[weekdayOf(date)]!
  return short ? n.slice(0, 3) : n
}

export function monthName(date: ISODate, short = false): string {
  const n = MONTH_NAMES[toDate(date).getUTCMonth()]!
  return short ? n.slice(0, 3) : n
}

/** 'Wednesday, September 16' — the journal's date line. */
export function formatLongDate(date: ISODate): string {
  return `${weekdayName(date)}, ${monthName(date)} ${toDate(date).getUTCDate()}`
}

/** 'Sep 16' */
export function formatShortDate(date: ISODate): string {
  return `${monthName(date, true)} ${toDate(date).getUTCDate()}`
}

/**
 * 'today' / 'tomorrow' / 'Thu' / 'Sep 24' / '2 days ago' — relative to a *given* today.
 * The caller passes today in; nothing in this file reads the clock, so every render is
 * reproducible and demo screenshots never drift.
 */
export function relativeDay(date: ISODate, today: ISODate): string {
  const delta = daysBetween(today, date)
  if (delta === 0) return 'today'
  if (delta === 1) return 'tomorrow'
  if (delta === -1) return 'yesterday'
  if (delta > 1 && delta < 7) return weekdayName(date, true)
  if (delta < -1 && delta > -7) return `${-delta} days ago`
  return formatShortDate(date)
}

/** Deterministic hash → [0,1). Same input, same output, forever, in every environment. */
export function seededUnit(key: string): number {
  let h = 2166136261
  for (let i = 0; i < key.length; i++) h = Math.imul(h ^ key.charCodeAt(i), 16777619)
  return ((h >>> 0) % 10000) / 10000
}
